import { Box, Stack, SxProps, Typography } from '@mui/material';
import { FormField } from 'common/ui';
import React, { FC, useMemo, useState } from 'react';
import { RadioGroup } from 'components/RadioGroup';
import { Button } from 'system/atoms';
import { usePostRest } from 'common/hooks';
import { ApiPaths } from 'modules/calendar/constants/apiPaths';
import { ToastType, useToast } from 'services/toast';
import { FailedMessageWithHref } from 'common/ui';
import { useGetOwnedUserCalendarsByUserQuery } from 'generated-graphql/hooks/apollo-hooks';
import { userAtom } from 'services/user/atoms';
import { useRecoilValue } from 'recoil';
import { Loading } from 'components/Loading';
import CheckIcon from '@mui/icons-material/Check';
import { UserCalendarProvider } from 'generated-graphql/types';
import { UserCalendarStatus } from 'generated-graphql/graphql';
import { useConfirmationModal } from 'services/modals/hooks';
import { useLoadingOverlay } from 'services/loading/hooks';
import { ConfirmationModalType } from 'services/modals/constants';
import { Link } from '@mui/material';
import { Paths } from 'services/navigation/paths';
import { RouterLink } from 'common/ui/RouterLink';

const connectContainer: SxProps = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 1,
  mt: '12px'
};

const calendarOptions = [
  { value: UserCalendarProvider.Microsoft, label: 'Microsoft Outlook' },
  { value: UserCalendarProvider.Google, label: 'Google' }
];

const connectableProviders = [
  UserCalendarProvider.Google,
  UserCalendarProvider.Microsoft
];

export const CalendarsTab: FC = () => {
  const user = useRecoilValue(userAtom);

  const [chosenProvider, setChosenProvider] = useState<UserCalendarProvider>();

  const { showToast } = useToast();
  const { showModal } = useConfirmationModal();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();

  const { data, loading, refetch } = useGetOwnedUserCalendarsByUserQuery({
    variables: {
      userId: user?.id || ''
    },
    skip: !user?.id
  });

  const { mutateAsync: connectAsync, isPending: isConnecting } = usePostRest<
    { url: string },
    { provider: string; location: string }
  >(ApiPaths.GENERATE_OAUTH_URL);

  const { mutateAsync: disconnectAsync } = usePostRest<void, { email: string }>(
    ApiPaths.DISCONNECT
  );

  // Only calendars the user owns (linked by userId), never calendars they
  // created for someone else (PAD-5732). Virtual calendars are Popp-managed
  // and can't be connected or disconnected here.
  const userCalendar = useMemo(() => {
    if (loading) return;

    const ownedCalendars =
      data?.getUserCalendarOrganizationByUser?.items?.flatMap((item) =>
        item?.userCalendar ? [item.userCalendar] : []
      ) ?? [];

    return ownedCalendars.find(
      (calendar) =>
        calendar.provider &&
        connectableProviders.includes(calendar.provider) &&
        calendar.status === UserCalendarStatus.Connected
    );
  }, [data, loading]);

  const handleConnect = async () => {
    try {
      // TODO: add new forms
      if (!chosenProvider) return;
      const baseUrl = window.location.origin;

      const result = await connectAsync({
        provider: chosenProvider,
        location: `${baseUrl}/profile?tab=calendars&connectionStatus=success`
      });
      window.open(result.url, '_blank');
    } catch (e) {
      showToast({
        title: 'Calendar link failed',
        message: (
          <FailedMessageWithHref
            failedMessage="Try linking your calendar again."
            emailSubject="Failed to link calendar"
          />
        ),
        type: ToastType.Error
      });
    }
  };

  const handleDisconnect = async () => {
    try {
      if (!userCalendar?.email) return;

      showLoadingOverlay();

      await disconnectAsync({
        email: userCalendar?.email
      });
      await refetch();
      showToast({
        title: 'Calendar disconnected',
        type: ToastType.Success
      });
    } catch (e) {
      showToast({
        title: 'Calendar disconnection failed',
        message: (
          <FailedMessageWithHref
            failedMessage="Try disconnecting your calendar again."
            emailSubject="Failed to disconnect calendar"
          />
        ),
        type: ToastType.Error
      });
    } finally {
      hideLoadingOverlay();
    }
  };

  const showDisconnectModal = () => {
    if (!userCalendar?.email) return;

    showModal({
      title: 'Are you sure?',
      message: `This will disconnect ${userCalendar.email}. Meeting templates this calendar is part of will stop working until it is reconnected.`,
      destructiveText: 'Disconnect',
      onDestructiveConfirm: handleDisconnect,
      cancelText: 'Cancel',
      modalType: ConfirmationModalType.DESTRUCTIVE
    });
  };

  const connectComponent = useMemo(() => {
    if (loading) {
      return [<Loading />];
    }

    if (userCalendar) {
      return [
        <Stack direction="column" spacing={2}>
          <Box sx={connectContainer}>
            <CheckIcon sx={{ color: 'success.contrastText', fontSize: 16 }} />
            <Typography>
              {userCalendar.provider === UserCalendarProvider.Google
                ? 'Google'
                : 'Microsoft Outlook'}{' '}
              is your linked calendar ({userCalendar.email}){' '}
              <Link href="#" onClick={showDisconnectModal}>
                Disconnect
              </Link>
            </Typography>
          </Box>
          <RouterLink to={Paths.HOME_CALENDAR_MEETINGS}>
            <Button variant="primary" sx={{ width: 'fit-content' }}>
              Go to meetings
            </Button>
          </RouterLink>
        </Stack>
      ];
    }

    return [
      <RadioGroup
        key="calendar-radio-group"
        name="calendar"
        label="Choose calendar"
        radioTypographyPropsOverride={{
          variant: 'body2',
          color: '#504955'
        }}
        labelContainer={{ mb: 1 }}
        options={calendarOptions}
        value={chosenProvider}
        onChange={(_, value) =>
          setChosenProvider(value as UserCalendarProvider)
        }
      />,
      <Button
        key="connect-calendar-button"
        onClick={handleConnect}
        variant="primary"
        loading={isConnecting}
        disabled={isConnecting}
      >
        Connect
      </Button>
    ];
  }, [userCalendar, loading, chosenProvider, loading]);

  return (
    <Box>
      <FormField
        title="Link your calendar"
        subTitle={
          <Typography>
            Allow candidates to book and manage meetings directly in Popp. If
            you use a different calendar type,{' '}
            <Link href="mailto:support@joinpopp.com?subject=Different calendar type">
              contact support
            </Link>
            .
          </Typography>
        }
        customComponents={connectComponent}
      />
    </Box>
  );
};
